import { useTranslation } from "react-i18next";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useController, useFormContext } from "react-hook-form";
import {
  parseResourceSrc,
  parsePreviewResourceSrc
} from "../../services/common-service";
import { PARTNER_LOGO_MODE } from "../strings";
import FileInput from "../reusable-form-fields/input-file";
import TextField from "../reusable-form-fields/text-field";
import PartnerBadgePreview from "./partner-badge-preview";
import { getPartnerBadge, getPartnerLogoMode } from "./partner-badge-layout";

// The number input gives text; keep a number (or nothing for "empty = 100 %")
// in the form, also when the Facebook tab's schema does not cast design.
const toScale = (value) =>
  value === "" || value === null || value === undefined
    ? undefined
    : Number(value);

const MODE_OPTIONS = [
  {
    value: PARTNER_LOGO_MODE.AUTO_LIGHT,
    label: "partnerLogoSection.autoLight"
  },
  { value: PARTNER_LOGO_MODE.AUTO_DARK, label: "partnerLogoSection.autoDark" },
  { value: PARTNER_LOGO_MODE.MANUAL, label: "partnerLogoSection.manual" }
];

// Partner logo of the classic funnel footer (Asana 1213410886347834):
// automatic badge with the company logo (light/black) or an own image.
// The uploaded image stays stored in automatic mode, so switching back
// to "Eigenes Bild" shows it again.
const PartnerLogoField = ({
  resourceId,
  parentFolderName,
  selectedCategory
}) => {
  const { t } = useTranslation();
  const {
    watch,
    register,
    formState: { errors }
  } = useFormContext();
  // no stored mode (funnels saved before) = own image
  const { field: modeField } = useController({
    name: "design.partnerLogoMode"
  });
  const mode = getPartnerLogoMode(modeField.value);
  const partnerLogo = watch("design.partnerLogo");
  // like the funnel footer: only an uploaded company logo (asset key)
  const companyLogoKey = watch("design.companyLogo")?.key;
  const companyLogoSrc = companyLogoKey
    ? `${process.env.REACT_APP_ASSET_CDN_URL}/${companyLogoKey}`
    : "";
  const displayOn = watch("design.partnerLogoDisplay");
  const badge = getPartnerBadge(mode, watch("design.partnerLogoScale"));

  return (
    <div className="mb-4">
      <div className="d-flex">
        <div className="mb-2">Partnerlogo</div>
        <div className="ms-2">
          <OverlayTrigger
            overlay={
              <Tooltip>{displayOn ? "Display On" : "Display Off"}</Tooltip>
            }
          >
            <Form.Check
              role="button"
              type="switch"
              {...register("design.partnerLogoDisplay")}
              className="float-end"
            />
          </OverlayTrigger>
        </div>
      </div>
      <div className="mb-2">
        {MODE_OPTIONS.map(({ value, label }) => (
          <Form.Check
            key={value}
            inline
            type="radio"
            id={`partnerLogoMode-${value}`}
            name="design.partnerLogoMode"
            label={t(label)}
            checked={mode === value}
            onChange={() => modeField.onChange(value)}
          />
        ))}
      </div>
      {badge ? (
        <div>
          {companyLogoSrc ? (
            <PartnerBadgePreview badge={badge} logoSrc={companyLogoSrc} />
          ) : (
            <div className="text-muted">
              {t("partnerLogoSection.missingCompanyLogo")}
            </div>
          )}
          <div className="mt-3" style={{ maxWidth: "260px" }}>
            <TextField
              label={t("partnerLogoSection.scale")}
              placeholder="100"
              type="number"
              name="design.partnerLogoScale"
              errors={errors?.design?.partnerLogoScale}
              register={(name) => register(name, { setValueAs: toScale })}
              blurOnScroll
              noSpacing
            />
          </div>
        </div>
      ) : (
        <FileInput
          label=""
          name="design.partnerLogo"
          resourceId={resourceId}
          parentFolderName={parentFolderName}
          url={parseResourceSrc(partnerLogo)}
          urlForPreview={parsePreviewResourceSrc(partnerLogo)}
          asset={partnerLogo?.asset}
          selectedCategory={selectedCategory}
          tagName="partnerlogo"
        />
      )}
    </div>
  );
};
export default PartnerLogoField;
